/**
 * Non-visual helpers for the Trace Studio panel: API client config, asset
 * import/open actions, publish-status and rename/delete mutations, and
 * upload type mapping.
 */

import type { BehaviorData } from 'trace-viewer/behaviors';
import { Renderer } from 'trace-viewer/behaviors';
import { parseCmsMetadata, topLevelObjects, isDocumentPlacement } from 'trace-viewer/editor';
import {
    DROPPABLE_ASSET_EXTENSIONS,
    getAssetCategory, toDotExtensions,
} from 'trace-viewer/scene';
import type { TraceScene } from 'trace-viewer/scene';
import type { ProjectResponseDto, SceneResponseDto } from 'trace-viewer/cms';
import { EntityType, Scenes, Projects } from 'trace-viewer/cms';
import { assetFileUrl } from 'trace-viewer/content';
import { isFolderAssetUrl } from './cms/asset-storage';
import { adoptDocumentRoot } from './cms/document-root';
import { CONTENT_STORAGE, type ContentStorage } from './config/contentStorage';
import { EDITOR_SCENE_DOT_EXTENSIONS, fileExtension, isSceneFileName } from './scene-formats';
import { showSpinnerToast } from './toast';
import { CmsClient } from './cms/CmsClient';
import { CmsManager } from './cms/CmsManager';
import { API_BASE_URL, ensureFreshToken, getCatalogToken } from './catalog-auth';

export const BLOB_BASE_URL = (import.meta.env.VITE_BLOB_URL as string || '').replace(/\/$/, '');

// ============================================================================
// Source Asset Tracking
// ============================================================================
// When a .trace asset is opened from the catalog, we track it so the export
// menu can offer one-click republish (update in place) via assetsPartialUpdate.

/** Asset + team context needed for PATCH permissions. */
export interface SourceAsset extends AssetItem {
    /** Team ID required by the PATCH endpoint for permission checks. */
    teamId?: string;
}

let _sourceAsset: SourceAsset | null = null;

/** Set the source asset (called by doOpenAsset on successful open). */
export function setSourceAsset(asset: SourceAsset | null): void { _sourceAsset = asset; }

/**
 * The asset the editor currently has open, or null.
 *
 * `EditorSession.sourceAsset` carries the identity a publish needs
 * (`assetId`, `teamId`, `title`); this carries the whole **row**, which is
 * the only place the asset's own `url` is — and that url is what says where
 * the asset's content folder is (`assetFolderBaseUrl`, spec §2.1).
 */
export function getSourceAsset(): SourceAsset | null { return _sourceAsset; }

/** Clear the source asset (called on new scene load / disconnect). */
export function clearSourceAsset(): void { _sourceAsset = null; }

// ============================================================================
// Utilities
// ============================================================================

/** Resolve a URL: return absolute URLs as-is, prefix relative paths with blob storage base. */
export function resolveUrl(url: string | null | undefined): string | undefined {
    if (!url) return undefined;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${BLOB_BASE_URL}/${url.replace(/^\//, '')}`;
}

/**
 * Create API client config with auth header.
 *
 * The `securityWorker` runs before every authenticated request and calls
 * `ensureFreshToken()` so a JWT near expiry is refreshed proactively. If
 * refresh fails the worker omits the Authorization header — the server will
 * 401, but auth state has already been cleared and a toast shown by
 * `ensureFreshToken()`, sending the panel back to login.
 *
 * `accessToken` is the token at construction time and is used as a fallback
 * if `ensureFreshToken()` returns null for some reason (it shouldn't).
 */
export function apiConfig(accessToken: string) {
    return {
        baseURL: API_BASE_URL,
        securityWorker: async () => {
            const fresh = await ensureFreshToken();
            const token = fresh ?? accessToken;
            return { headers: { Authorization: `Bearer ${token}` } };
        },
        secure: true,
    };
}

/**
 * File-level metadata the CMS stores alongside an asset. Well-known keys
 * (`size`, `readableSize`, `duration`) are surfaced directly; any additional
 * keys the backend adds later flow through untouched for generic display.
 */
export interface AssetFileMetadata {
    /** File size in bytes. */
    size?: number;
    /** Human-readable size string produced by the backend (e.g. "1.42 MB"). */
    readableSize?: string;
    /** Media duration in seconds (audio/video). */
    duration?: number;
    [key: string]: unknown;
}

/** Minimal asset shape shared by CatalogAssetResponseDto and AssetResponseDto. */
export interface AssetItem {
    assetId?: string;
    title?: string | null;
    url?: string | null;
    thumbnailUrl?: string | null;
    originalFilename?: string | null;
    /** Behaviors parsed from CMS metadata at the API boundary. */
    behaviors?: BehaviorData[];
    /** File metadata (size, duration, …) parsed at the API boundary. */
    fileMetadata?: AssetFileMetadata | null;
    fileType?: string | null;
    dateCreated?: string;
    dateModified?: string | null;
    entityType?: string;
    creator?: { email?: string | null };
}

/** Raw asset shape from CMS API (CatalogAssetResponseDto / AssetResponseDto). */
interface CmsAssetDto {
    assetId?: string;
    title?: string | null;
    url?: string | null;
    thumbnailUrl?: string | null;
    originalFilename?: string | null;
    metadata?: unknown;
    /** JSON string (or already-parsed object) of file-level metadata. */
    fileMetadata?: unknown;
    fileType?: string | null;
    dateCreated?: string;
    dateModified?: string | null;
    entityType?: string;
    creator?: { email?: string | null };
}

export { parseCmsMetadata } from 'trace-viewer/editor';

/**
 * Parse the CMS `fileMetadata` field. The backend serializes it as a JSON
 * string, but tolerate an already-parsed object (and null/garbage) too.
 */
export function parseFileMetadata(raw: unknown): AssetFileMetadata | null {
    if (raw == null) return null;
    let value: unknown = raw;
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed) return null;
        try {
            value = JSON.parse(trimmed);
        } catch {
            return null;
        }
    }
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
    return value as AssetFileMetadata;
}

/**
 * Convert a CMS API DTO into an AssetItem, parsing the raw `metadata`
 * field into a typed `behaviors` array and `fileMetadata` into a typed record.
 */
export function toAssetItem(dto: CmsAssetDto): AssetItem {
    const { metadata, fileMetadata, ...rest } = dto;
    return { ...rest, behaviors: parseCmsMetadata(metadata), fileMetadata: parseFileMetadata(fileMetadata) };
}

/** How a library row is placed — the one decision point for `import` and `open`. */
export interface AssetPlacement {
    /** The url handed to `viewer.import` / `viewer.open`: the node's `src`. */
    url: string;
    /**
     * Whether `url` is the live `<api>/assets/{id}/file` reference (spec §3.4)
     * rather than the row's own file. A reference is never unpacked on
     * placement; a file is not re-resolved by anyone.
     */
    isLibraryReference: boolean;
    /** Whether the row's file is a scene package (`.trace`/`.tracex`/`.zip`…) rather than a single asset. */
    isScenePackage: boolean;
    /**
     * Whether `url` resolves to a **document** — row 2 of the reference rule
     * table (SCENE_ROOT_SPEC §2.3): the placement carries only what the scene
     * put on it and the document's own root is its child, with its own
     * behaviors.
     *
     * Read off the row, never fetched: a **folder** row is the document the
     * server writes (`trace-content/assets/{id}/<hash>.gltf`), and a scene
     * package (`.trace`/`.zip`/…) is a document too. Everything else is a
     * **file** (row 1) — a raw blob the placement owns outright, which is why
     * a legacy row's `metadata.behaviors` still belong on it.
     */
    isDocument: boolean;
}

/**
 * Decide the url a library row is placed by (CONTENT_MODEL_SPEC §3.4, §6.1).
 *
 * A **reference** is `<api>/assets/{id}/file`, which 302s to the asset's
 * current file. It is the asset's **identity**, not a snapshot of it:
 * nothing is copied, and editing the asset reaches every scene that names
 * it. The row's own `url` names one version's bytes and would freeze the
 * placement to them.
 *
 * Which rows get one depends on the write mode (`VITE_CONTENT_STORAGE`) and
 * on the row:
 *
 * - In **folder** mode every row with an id is referenced. `/file` exists
 *   wherever folder writes do, it answers for a legacy raw-blob row too
 *   (302 to the blob), and the manifest pre-flight refuses a direct storage
 *   url at save time (§2.5) — so the row's own url is not even an option.
 * - In **legacy** mode — the default, and what prod runs while its API is
 *   release #885, which has no `/file` at all — only a **folder** row is
 *   referenced, because a folder document has no other address worth
 *   naming. A legacy row is placed by its own file, exactly as it was
 *   before the content model; nothing re-resolves a raw blob, and the
 *   endpoint may not be there. Which form a row is in is read off the row
 *   (`isFolderAssetUrl`), so both coexist in one library.
 *
 * A row with an id but no url has only the reference to be addressed by;
 * a row with no id has only its url.
 */
export function resolveAssetPlacement(
    asset: AssetItem,
    mode: ContentStorage = CONTENT_STORAGE,
): AssetPlacement | undefined {
    // The reference URL has no extension, so the row's own filename is what
    // says whether this is a scene format.
    const isScenePackage = isSceneFileName(asset.originalFilename ?? asset.url ?? '');
    const referenced = mode === 'folder' || isFolderAssetUrl(asset.url) || !asset.url;

    // What the url RESOLVES to, which is not what it points at: a folder row's
    // `/file` 302s to the asset's document, a legacy row's to its raw blob.
    const isDocument = isFolderAssetUrl(asset.url) || isScenePackage;

    if (asset.assetId && referenced) {
        try {
            return {
                url: assetFileUrl(API_BASE_URL, asset.assetId),
                isLibraryReference: true,
                isScenePackage,
                isDocument,
            };
        } catch (error) {
            console.warn(`[Catalog] Asset ${asset.assetId} has no usable file URL:`, error);
        }
    }
    const url = resolveUrl(asset.url);
    return url ? { url, isLibraryReference: false, isScenePackage, isDocument } : undefined;
}

/** The url a scene references a library row by. See {@link resolveAssetPlacement}. */
export function assetReferenceUrl(asset: AssetItem, mode: ContentStorage = CONTENT_STORAGE): string | undefined {
    return resolveAssetPlacement(asset, mode)?.url;
}

/**
 * The behaviors a placement may be imported with — the row's CMS metadata, or
 * nothing (SCENE_ROOT_SPEC §2.3).
 *
 * `asset.behaviors` is a LEGACY row's `metadata.behaviors` (spec §3.6: new
 * rows carry none, the server strips the column on write). Under the
 * reference rule those land on the **placement**, and for a document asset
 * that is wrong twice over: the document's root already declares them, and a
 * copy on the placement SHADOWS the root's declaration — with `inherits: true`
 * the inner, real declaration loses (Canopy step 4), which is exactly the ESPN
 * Remote bug (an AutoUI slider committing into a declaration that drives
 * nothing).
 *
 * So they are passed only where they still belong to the node that gets them:
 *
 * - a **file** placement (a raw-blob leaf row) — row 1, the node owns the file;
 * - an **unpacked** import — the content becomes this scene's own and
 *   `unpackReference` carries the caller's behaviors onto the promoted root,
 *   not onto a placement;
 * - a **legacy session** — the stand-in import there makes the document's root
 *   BE the handle, so nothing is shadowed and today's behaviour is kept
 *   (§2.3 "Scope — forward-only").
 */
export function placementMetadataBehaviors(
    placement: AssetPlacement,
    behaviors: BehaviorData[] | undefined,
    unpack: boolean,
    traceScene: TraceScene | null | undefined,
): BehaviorData[] | undefined {
    if (!behaviors?.length) return undefined;
    if (!placement.isDocument || unpack) return behaviors;
    // A scene with no session yet is new, and a new scene is a 2.1 session.
    return (traceScene?.placementRuleActive ?? true) ? undefined : behaviors;
}

/**
 * Place a library asset in the scene (spec §3.4, §3.8, §6.1).
 *
 * A folder row is placed **as a reference**: no bytes are copied and nothing
 * is unpacked — the node carries the asset file URL and the asset stays
 * live. Freezing a placement — copying the asset's folder into the scene's
 * and inlining its content — is the explicit "Copy to scene" gesture, not a
 * side effect of dragging. A legacy row is placed by its own file, the way
 * it was before the content model (`resolveAssetPlacement`).
 */
export async function doImportAsset(
    asset: AssetItem,
    setInsertingId: (id: string | null) => void,
) {
    const placement = resolveAssetPlacement(asset);
    if (!placement) return;

    const viewer = document.querySelector('trace-viewer');
    if (!viewer?.import) {
        console.error('[Catalog] No trace-viewer found');
        return;
    }

    setInsertingId(asset.assetId ?? null);
    const dismiss = showSpinnerToast(`Importing ${asset.title ?? 'asset'}...`);

    try {
        const name = asset.title ?? asset.originalFilename?.replace(/\.[^/.]+$/, '') ?? 'CatalogAsset';
        // A legacy scene package (`.trace`/`.tracex`/`.zip`) is not a live
        // reference — nothing re-resolves a raw blob — so it dissolves into
        // this scene's own objects, as it did before the content model and
        // as TraceFrontend does for the same row. A library reference is
        // never unpacked here: freezing one is the explicit "Copy to scene"
        // gesture (§3.8), and an unconditional unpack would dissolve it.
        const unpack = placement.isScenePackage && !placement.isLibraryReference;
        const { sceneObject } = await viewer.import(
            placement.url,
            name,
            placementMetadataBehaviors(placement, asset.behaviors, unpack, viewer.traceScene),
            unpack ? { unpack: true } : undefined,
        );

        // A reference to a DOCUMENT is a placement: the asset's own root is
        // its child and owns the geometry (SCENE_ROOT_SPEC §2.3 row 2), so a
        // `Renderer` here would reach no mesh (`getOwnedMeshes` is
        // own-meshes-only) and recolouring the asset is an edit INSIDE the
        // reference — the copy gesture, not a side effect of a drag. The
        // bounding box is read off the hierarchy either way.
        if (!placement.isScenePackage) {
            const { min, max } = sceneObject.transform.getHierarchyBoundingVectors(true);
            // A node with no meshes under it answers with Babylon's EMPTY
            // extent — min = +Number.MAX_VALUE, max = -Number.MAX_VALUE, an
            // inverted box rather than a degenerate one — and subtracting that
            // puts the object at y = -1.8e308, somewhere it can never be found
            // or dragged back from. (`isFinite` does not catch it: MAX_VALUE
            // is a finite number.) Nothing to sit on the ground means nothing
            // to move: leave the placement where it is, so whatever failed to
            // load is the visible problem.
            if (min.y <= max.y) {
                sceneObject.transform.position.y -= min.y;
            } else {
                console.warn(`[Catalog] "${sceneObject.name}" imported with no renderable bounds — leaving it at the origin`);
            }
            // Add Renderer behavior for material editing support (if not already present from asset metadata)
            if (!isDocumentPlacement(sceneObject) && !sceneObject.getBehavior(Renderer)) {
                await sceneObject.addBehavior(Renderer);
            }
        }

        window.dispatchEvent(new CustomEvent('trace-catalog:import', { detail: { sceneObject } }));
        dismiss(`Imported ${sceneObject.name}`, 'success');
    } catch (err) {
        console.error('[Catalog] Import failed:', err);
        dismiss('Import failed', 'error');
    } finally {
        setInsertingId(null);
    }
}

/**
 * Whether the current scene has any content (i.e. is non-empty).
 *
 * Content is what the DOCUMENT ROOT holds: every session has one and an empty
 * scene has one too (D8), so the roots themselves never count as content.
 */
export function sceneHasContent(): boolean {
    const viewer = document.querySelector('trace-viewer');
    return topLevelObjects(viewer?.traceScene).length > 0;
}

/**
 * Open a library asset for editing, replacing any current scene.
 *
 * A folder row is opened through the asset file URL, not its blob url: the
 * redirect lands on the asset's **document**, and the viewer resolves that
 * document's relative `assets/` against where it landed (spec §3.4). Editing
 * an asset is therefore editing a folder document — the same thing a scene
 * is — and saving it writes JSON, not a repacked archive.
 *
 * A legacy raw-leaf row is opened by its own file url, and the viewer's
 * single-asset path handles it exactly as before (`resolveAssetPlacement`).
 */
export async function doOpenAsset(
    asset: AssetItem,
    setInsertingId: (id: string | null) => void,
    teamId?: string,
) {
    const url = assetReferenceUrl(asset);
    if (!url) return;

    const viewer = document.querySelector('trace-viewer');
    if (!viewer?.open) {
        console.error('[Catalog] No trace-viewer found');
        return;
    }

    setInsertingId(asset.assetId ?? null);
    const dismiss = showSpinnerToast(`Opening ${asset.title ?? 'asset'}...`);

    try {
        // Clear immediately so the user sees a blank scene while loading
        // (viewer.open fetches first for non-bundle formats — clearScene
        // gives consistent UX across all asset types)
        viewer.clearScene();
        // `revalidate`: this is the EDITOR opening an asset it can publish to,
        // and the reference it opens by is a 302 the browser may serve from
        // cache for a minute (spec §2.7). Without this, reopening an asset
        // just after publishing it shows the pre-publish document — the edit
        // looks lost, then appears a minute later. Only this fetch
        // revalidates; the folder's content-addressed files stay cacheable
        // forever.
        await viewer.open({ src: url, behaviors: asset.behaviors, revalidate: true });
        // Every session has a document root (D8), and it is adopted BEFORE the
        // event below — the handler's snapshot baseline is taken on the adopted
        // tree, so a freshly opened document is not dirty.
        await adoptDocumentRoot(viewer.traceScene, asset.title);
        const sourceAsset: SourceAsset = { ...asset, teamId };
        setSourceAsset(sourceAsset);
        window.dispatchEvent(new CustomEvent('trace-catalog:open', { detail: { asset: sourceAsset } }));
        dismiss(`Opened ${asset.title ?? 'asset'}`, 'success');
    } catch (err) {
        console.error('[Catalog] Open failed:', err);
        dismiss('Failed to open asset', 'error');
    } finally {
        setInsertingId(null);
    }
}

/**
 * Open a CMS Scene by sceneId. Constructs a CmsManager and runs the same
 * load flow that the URL-bootstrapped path uses, then dispatches an event so
 * the editor host can wire up the publish UI.
 */
export async function doOpenScene(
    scene: SceneResponseDto,
    setInsertingId: (id: string | null) => void,
) {
    if (!scene.id) return;

    const viewer = document.querySelector('trace-viewer');
    if (!viewer?.open) {
        console.error('[Catalog] No trace-viewer found');
        return;
    }

    setInsertingId(scene.id);
    const dismiss = showSpinnerToast(`Opening ${scene.name ?? 'scene'}...`);

    try {
        const cms = new CmsManager(
            new CmsClient(getCatalogToken, API_BASE_URL, BLOB_BASE_URL),
            scene.id,
            BLOB_BASE_URL,
        );
        await cms.loadScene(viewer, () => { /* host wires up via the event below */ });

        window.dispatchEvent(new CustomEvent<CatalogOpenSceneDetail>(
            'trace-catalog:open',
            { detail: { sceneId: scene.id, cms } },
        ));
        dismiss(`Opened ${scene.name ?? 'scene'}`, 'success');
    } catch (err) {
        console.error('[Catalog] Open scene failed:', err);
        dismiss('Failed to open scene', 'error');
    } finally {
        setInsertingId(null);
    }
}

/** Detail shape for the `trace-catalog:open` event when a Scene is opened. */
export interface CatalogOpenSceneDetail {
    sceneId: string;
    cms: CmsManager;
}

// Two deliberately distinct public hosts for a short code:
//   - shareUrl → trce.ch, the App Clip universal-link domain. Meant to be
//     scanned/opened on a phone (launches the native AR experience; viewer-app
//     itself links there as its mobile-without-WebXR fallback).
//   - viewerUrl → viewer.trace3d.app, the web viewer. Meant for clicking in a
//     desktop browser.
// QR codes use shareUrl (scanned by phones); inline short-code links use
// viewerUrl (clicked on desktop). Don't "unify" them.

/** Public share URL (App Clip link) for a scene or project short code. */
export function shareUrl(kind: 'scenes' | 'projects', shortCode: string): string {
    return `https://trce.ch/${kind}/${shortCode}`;
}

/** Public web-viewer URL for a short code (scenes and projects share the same bare path). */
export function viewerUrl(shortCode: string): string {
    return `https://viewer.trace3d.app/${shortCode}`;
}

// ============================================================================
// Upload type mapping
// ============================================================================

/**
 * Accepted file extensions for drag/drop upload into Content folders
 * (`POST /assets`). `.glz` is the bundle the editor writes; `.tracex`/`.glxf`
 * stay accepted as read-only inputs until trace-viewer#816 retires the readers.
 */
export const UPLOAD_EXTENSIONS = [
    ...toDotExtensions(DROPPABLE_ASSET_EXTENSIONS),
    ...EDITOR_SCENE_DOT_EXTENSIONS,
];

/** Category → CMS EntityType mapping. */
const CATEGORY_TO_ENTITY_TYPE: Record<string, EntityType> = {
    model: EntityType.Model, splat: EntityType.Model,
    image: EntityType.Image, video: EntityType.Video,
    audio: EntityType.Audio,
    scene: EntityType.Trace,
};

/** Map a filename to the CMS EntityType for upload based on its extension. Returns null for unknown types. */
export function extensionToEntityType(filename: string): EntityType | null {
    // Scene formats first: the viewer's `getAssetCategory` still predates
    // `.glz`, and a bundle uploaded as `EntityType.None` is a row nothing can
    // find again.
    if (isSceneFileName(filename)) return EntityType.Trace;
    const ext = fileExtension(filename);
    return CATEGORY_TO_ENTITY_TYPE[getAssetCategory(ext)] ?? null;
}

// ============================================================================
// Publish status (ActivationStatus)
// ============================================================================

// Backend enum: TraceCms.Domain.Enums.ActivationStatusType, serialized as enum name.
export type PublishStatus = 'UnPublished' | 'PublishedWithLimitedAccess' | 'Published' | 'OpenPreview';

export const PUBLISH_STATUS_LABELS: Record<PublishStatus, string> = {
    UnPublished: 'Unpublished',
    PublishedWithLimitedAccess: 'Limited access',
    Published: 'Published',
    OpenPreview: 'Open preview',
};

export const PUBLISH_STATUS_ORDER: PublishStatus[] = ['UnPublished', 'PublishedWithLimitedAccess', 'Published', 'OpenPreview'];

export function normalizeStatus(value: string | null | undefined): PublishStatus {
    if (value && (PUBLISH_STATUS_ORDER as string[]).includes(value)) return value as PublishStatus;
    return 'UnPublished';
}

/**
 * PATCH a scene's ActivationStatus. Returns the updated scene on success.
 *
 * Refuses to send when `scene.name` is missing: the server's generated
 * PATCH contract marks `Name` as required and treats a non-absent value
 * as authoritative, so falling back to `''` would silently overwrite the
 * stored name with empty string.
 */
export async function updateSceneStatus(
    token: string,
    scene: Pick<SceneResponseDto, 'id' | 'name'>,
    teamId: string,
    status: PublishStatus,
): Promise<SceneResponseDto> {
    if (!scene.id) throw new Error('Scene id missing');
    if (!scene.name) throw new Error('Scene name missing — refusing to publish-status update without it (would overwrite stored name with empty string)');
    const client = new Scenes(apiConfig(token));
    const res = await client.scenesPartialUpdate(scene.id, {
        TeamId: teamId,
        Name: scene.name,
        ActivationStatus: status,
    });
    return res.data;
}

/**
 * PATCH a project's ActivationStatus. Returns the updated project on success.
 * Same Name-required guard as {@link updateSceneStatus}.
 */
export async function updateProjectStatus(
    token: string,
    project: Pick<ProjectResponseDto, 'id' | 'name'>,
    teamId: string,
    status: PublishStatus,
): Promise<ProjectResponseDto> {
    if (!project.id) throw new Error('Project id missing');
    if (!project.name) throw new Error('Project name missing — refusing to publish-status update without it (would overwrite stored name with empty string)');
    const client = new Projects(apiConfig(token));
    const res = await client.projectsPartialUpdate(project.id, {
        TeamId: teamId,
        Name: project.name,
        ActivationStatus: status,
    });
    return res.data;
}

// ============================================================================
// Rename / delete mutations
// ============================================================================
// PATCH semantics: absent fields are unchanged (see updateSceneStatus), so a
// rename sends only TeamId + Name.

/** PATCH a scene's name. Returns the updated scene. */
export async function renameScene(
    token: string,
    sceneId: string,
    teamId: string,
    name: string,
): Promise<SceneResponseDto> {
    const client = new Scenes(apiConfig(token));
    const res = await client.scenesPartialUpdate(sceneId, { TeamId: teamId, Name: name });
    return res.data;
}

/**
 * Delete a scene (server-side soft delete). If it was the last scene of a
 * project, the server deletes the project too — refresh project lists after.
 */
export async function deleteScene(token: string, sceneId: string, teamId: string): Promise<void> {
    const client = new Scenes(apiConfig(token));
    await client.scenesDelete(sceneId, { teamId });
}

/** PATCH a project's name. Returns the updated project. */
export async function renameProject(
    token: string,
    projectId: string,
    teamId: string,
    name: string,
): Promise<ProjectResponseDto> {
    const client = new Projects(apiConfig(token));
    const res = await client.projectsPartialUpdate(projectId, { TeamId: teamId, Name: name });
    return res.data;
}

/** Delete a project (server-side soft delete). */
export async function deleteProject(token: string, projectId: string, teamId: string): Promise<void> {
    const client = new Projects(apiConfig(token));
    await client.projectsDelete(projectId, { teamId });
}

/**
 * Extract a human-readable message from an axios error. The CMS exception
 * middleware serializes BadRequestException as `{ message }` on 400 responses
 * (e.g. `PartProjectSceneError` when a scene attached to a project has its
 * activation status changed individually). Falls back to the Error message.
 */
export function extractServerErrorMessage(err: unknown): string | null {
    if (err && typeof err === 'object') {
        const e = err as { response?: { data?: unknown }; message?: unknown };
        const data = e.response?.data;
        if (data && typeof data === 'object') {
            const msg = (data as { message?: unknown }).message;
            if (typeof msg === 'string' && msg.length > 0) return msg;
        }
        if (typeof e.message === 'string' && e.message.length > 0) return e.message;
    }
    return null;
}
